import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import BookingStepper from './BookingStepper';
import { usePropertyId } from '../../../common/hooks/usePropertyId';
import { useReservationsData } from '../../../common/hooks/useReservationsData';
import { useAlertNotification } from '../../../common/hooks/useAlertNotification';
import {
  DatesAndAccommodationStep,
  CustomerDetailsStep,
} from './booking';
import { ORDER_STATUS } from '../../../common/enums';

const BOOKING_STEPS = [
  { id: 'dates', label: 'Termin i zakwaterowanie', Component: DatesAndAccommodationStep },
  { id: 'customer', label: 'Dane osobowe', Component: CustomerDetailsStep },
  { id: 'payment', label: 'Płatność' },
];

const emptyBooking = {
  checkin: null,
  checkout: null,
  adults: 2,
  selectedRoom: null,
  selectedPlan: null,
  totalPrice: 0,
  paymentMethod: null,
  error: null,
  customer: {
    firstName: '',
    lastName: '',
    email: '',
    countryPrefix: '+48',
    phoneNumber: '',
    address: '',
    countryCode: 'PL',
    comments: '',
  },
};

const BookingWizard = () => {
  const propertyId = usePropertyId();
  const navigate = useNavigate();
  const { createReservation } = useReservationsData(propertyId, undefined, { fetch: false });
  const { setAlert } = useAlertNotification();

  const [stepIndex, setStepIndex] = useState(0);
  const [booking, setBooking] = useState(emptyBooking);

  const step = BOOKING_STEPS[stepIndex];
  const stepId = step?.id;

  const goToCheckout = useCallback(async () => {
    const payload = {
      checkin: booking.checkin instanceof Date ? booking.checkin : new Date(booking.checkin),
      checkout: booking.checkout instanceof Date ? booking.checkout : new Date(booking.checkout),
      customer: {
        firstName: (booking.customer.firstName ?? '').trim(),
        lastName: (booking.customer.lastName ?? '').trim(),
        email: (booking.customer.email ?? '').trim(),
        countryPrefix: (booking.customer.countryPrefix ?? '+48').trim(),
        phoneNumber: (booking.customer.phoneNumber ?? '').trim().replace(/\s/g, ''),
        address: (booking.customer.address ?? '').trim() || undefined,
        countryCode: (booking.customer.countryCode ?? '').trim() || undefined,
      },
      planId: booking.selectedPlan?.planId,
      roomId: booking.selectedRoom,
      adults: booking.adults || 1,
      comments: (booking.customer.comments ?? '').trim() || undefined,
      status: ORDER_STATUS.DRAFT,
    };
    try {
      const order = await createReservation(payload);
      navigate(`/reservation/${order.id}/checkout`);
    } catch (error) {
      console.error(error);
      setAlert({
        message: 'Nie udało się utworzyć rezerwacji. Spróbuj ponownie.',
        severity: 'error',
      });
    }
  }, [booking]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = useCallback((propertyName, newValue) => {
    setBooking((prev) => ({ ...prev, [propertyName]: newValue }));
  }, []);

  /**
   * Called by step components when the user clicks "Next".
   * - dates → customer: advance internal step, keep /book?search params
   * - customer → creates a DRAFT order, then navigates to /reservation/:id/checkout
   */
  const handleSubmit = useCallback(async () => {
    if (stepId === 'dates') {
      setStepIndex(stepIndex + 1);
    }
    else if (stepId === 'customer') {
      goToCheckout();
    }
  }, [stepId, stepIndex, goToCheckout]);

  const handleBack = useCallback(() => {
    setStepIndex(stepIndex - 1);
  }, [stepIndex]);

  const StepComponent = step?.Component;
  if (!StepComponent) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <BookingStepper activeStep={stepIndex} />
      <StepComponent
        booking={booking}
        onSubmit={handleSubmit}
        onBack={handleBack}
        onChange={onChange}
      />
    </Box>
  );
};

export default BookingWizard;
